/**
 * Hex grid math for KNGDM.IO.
 *
 * ORIENTATION: **pointy-top**.
 * COORDINATES: **axial** (q, r), with cube (x, y, z) used internally for
 * rounding, distance and line-drawing.
 *
 * These two choices are fixed for the whole codebase. Per
 * `docs/02_TECHNICAL_ARCHITECTURE.md` this module is the single source of truth
 * so client rendering and server simulation agree without a translation layer.
 * Mixing orientations or introducing offset coordinates anywhere downstream is
 * the single most expensive bug available in this project — don't.
 *
 * Axial <-> cube:  x = q,  z = r,  y = -x - z   (so x + y + z === 0)
 *
 * `size` throughout is the hex **circumradius** (centre to corner). For a
 * pointy-top hex that means:
 *   width  = sqrt(3) * size   (flat-to-flat, horizontal)
 *   height = 2 * size         (point-to-point, vertical)
 *   horizontal spacing = sqrt(3) * size
 *   vertical spacing   = 1.5 * size
 */
export const SQRT3 = Math.sqrt(3);
// ---------------------------------------------------------------------------
// Construction & conversion
// ---------------------------------------------------------------------------
export function hex(q, r) {
    return { q, r };
}
export function toCube(h) {
    return { x: h.q, y: -h.q - h.r, z: h.r };
}
export function fromCube(c) {
    return { q: c.x, r: c.z };
}
// ---------------------------------------------------------------------------
// Identity & keying
// ---------------------------------------------------------------------------
/**
 * Canonical string key for Map/Set storage. Every hex-keyed structure in the
 * codebase uses this — decided once, here, deliberately.
 */
export function hexKey(h) {
    return `${h.q},${h.r}`;
}
export function parseHexKey(key) {
    const comma = key.indexOf(",");
    if (comma === -1)
        throw new Error(`Malformed hex key: ${JSON.stringify(key)}`);
    const q = Number(key.slice(0, comma));
    const r = Number(key.slice(comma + 1));
    if (!Number.isFinite(q) || !Number.isFinite(r)) {
        throw new Error(`Malformed hex key: ${JSON.stringify(key)}`);
    }
    return { q, r };
}
export function hexEquals(a, b) {
    return a.q === b.q && a.r === b.r;
}
// ---------------------------------------------------------------------------
// Arithmetic
// ---------------------------------------------------------------------------
export function hexAdd(a, b) {
    return { q: a.q + b.q, r: a.r + b.r };
}
export function hexSubtract(a, b) {
    return { q: a.q - b.q, r: a.r - b.r };
}
export function hexScale(h, factor) {
    return { q: h.q * factor, r: h.r * factor };
}
// ---------------------------------------------------------------------------
// Neighbours
// ---------------------------------------------------------------------------
/**
 * The six axial direction vectors in rotational order, starting due east and
 * proceeding counter-clockwise. The order matters: `hexRing` relies on it.
 */
export const HEX_DIRECTIONS = [
    { q: 1, r: 0 }, // 0 — E
    { q: 1, r: -1 }, // 1 — NE
    { q: 0, r: -1 }, // 2 — NW
    { q: -1, r: 0 }, // 3 — W
    { q: -1, r: 1 }, // 4 — SW
    { q: 0, r: 1 }, // 5 — SE
];
export const HEX_DIRECTION_COUNT = 6;
/** Direction index 0-5, matching `HEX_DIRECTIONS`. Wraps, so 6 === 0. */
export function hexDirection(index) {
    const wrapped = ((index % HEX_DIRECTION_COUNT) + HEX_DIRECTION_COUNT) % HEX_DIRECTION_COUNT;
    const direction = HEX_DIRECTIONS[wrapped];
    /* c8 ignore next */
    if (direction === undefined)
        throw new Error(`Unreachable direction index: ${index}`);
    return direction;
}
export function hexNeighbor(h, direction) {
    return hexAdd(h, hexDirection(direction));
}
export function neighbors(h) {
    return HEX_DIRECTIONS.map((d) => hexAdd(h, d));
}
// ---------------------------------------------------------------------------
// Distance & rounding
// ---------------------------------------------------------------------------
export function hexDistance(a, b) {
    const dq = a.q - b.q;
    const dr = a.r - b.r;
    return (Math.abs(dq) + Math.abs(dq + dr) + Math.abs(dr)) / 2;
}
/** Round fractional axial coordinates to the nearest hex, via cube rounding. */
export function hexRound(qf, rf) {
    const xf = qf;
    const zf = rf;
    const yf = -qf - rf;
    let rx = Math.round(xf);
    let ry = Math.round(yf);
    let rz = Math.round(zf);
    const dx = Math.abs(rx - xf);
    const dy = Math.abs(ry - yf);
    const dz = Math.abs(rz - zf);
    if (dx > dy && dx > dz) {
        rx = -ry - rz;
    }
    else if (dy > dz) {
        ry = -rx - rz;
    }
    else {
        rz = -rx - ry;
    }
    // `|| 0` normalises -0 to 0 so hexKey() never produces "-0,3".
    return { q: rx || 0, r: rz || 0 };
}
// ---------------------------------------------------------------------------
// Pixel conversion (pointy-top)
// ---------------------------------------------------------------------------
export function hexToPixel(h, size) {
    return {
        x: size * SQRT3 * (h.q + h.r / 2),
        y: size * 1.5 * h.r,
    };
}
export function pixelToHex(p, size) {
    const qf = ((SQRT3 / 3) * p.x - p.y / 3) / size;
    const rf = ((2 / 3) * p.y) / size;
    return hexRound(qf, rf);
}
/** The six corner points of a hex, for rendering. Corner 0 is due east-ish. */
export function hexCorners(h, size) {
    const centre = hexToPixel(h, size);
    const corners = [];
    for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 180) * (60 * i - 30);
        corners.push({
            x: centre.x + size * Math.cos(angle),
            y: centre.y + size * Math.sin(angle),
        });
    }
    return corners;
}
// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------
/**
 * The hexes at exactly `radius` from `centre`, walking the ring in order.
 * `hexRing(c, 0)` is `[c]`. Length is always `6 * radius` for radius >= 1.
 */
export function hexRing(centre, radius) {
    if (radius < 0 || !Number.isInteger(radius)) {
        throw new Error(`hexRing radius must be a non-negative integer, got ${radius}`);
    }
    if (radius === 0)
        return [{ q: centre.q, r: centre.r }];
    const results = [];
    let current = hexAdd(centre, hexScale(hexDirection(4), radius));
    for (let direction = 0; direction < 6; direction++) {
        for (let step = 0; step < radius; step++) {
            results.push(current);
            current = hexNeighbor(current, direction);
        }
    }
    return results;
}
/**
 * Every hex within `radius` of `centre`, centre first, then ring by ring.
 * This is the auto-claim-radius and area-of-effect primitive.
 */
export function hexSpiral(centre, radius) {
    const results = [{ q: centre.q, r: centre.r }];
    for (let ring = 1; ring <= radius; ring++) {
        results.push(...hexRing(centre, ring));
    }
    return results;
}
/** Linear interpolation between two hexes, rounded. Used by `hexLine`. */
function hexLerp(a, b, t) {
    return hexRound(a.q + (b.q - a.q) * t, a.r + (b.r - a.r) * t);
}
/**
 * The hexes along the straight line from `a` to `b`, inclusive of both ends.
 * Used by the click-and-drag surge verb to turn a drag into border segments.
 */
export function hexLine(a, b) {
    const steps = hexDistance(a, b);
    if (steps === 0)
        return [{ q: a.q, r: a.r }];
    // Nudge off exact edge-midpoints so ties round consistently rather than
    // producing a visibly jagged line.
    const epsilonA = { q: a.q + 1e-6, r: a.r + 1e-6 };
    const epsilonB = { q: b.q + 1e-6, r: b.r + 1e-6 };
    const results = [];
    for (let i = 0; i <= steps; i++) {
        results.push(hexLerp(epsilonA, epsilonB, i / steps));
    }
    return results;
}
// ---------------------------------------------------------------------------
// Viewport culling
// ---------------------------------------------------------------------------
/**
 * Every hex whose centre could fall inside the given pixel-space rectangle,
 * plus a one-hex margin so partially-visible hexes at the edges are included.
 *
 * This is the primitive Phase 1 rendering culls with — per
 * `docs/02_TECHNICAL_ARCHITECTURE.md`, never build display objects for the
 * whole map. Recompute on pan/zoom, not per frame.
 */
export function hexesInRect(topLeft, bottomRight, size) {
    if (size <= 0)
        throw new Error(`hexesInRect size must be positive, got ${size}`);
    const rowHeight = 1.5 * size;
    const colWidth = SQRT3 * size;
    const rMin = Math.floor(topLeft.y / rowHeight) - 1;
    const rMax = Math.ceil(bottomRight.y / rowHeight) + 1;
    const results = [];
    for (let r = rMin; r <= rMax; r++) {
        const qMin = Math.floor(topLeft.x / colWidth - r / 2) - 1;
        const qMax = Math.ceil(bottomRight.x / colWidth - r / 2) + 1;
        for (let q = qMin; q <= qMax; q++) {
            results.push({ q, r });
        }
    }
    return results;
}
// ---------------------------------------------------------------------------
// Rectangular map bounds
// ---------------------------------------------------------------------------
/**
 * The axial coordinates of a `width` x `height` rectangular map — the shape
 * `generateMap` produces in Phase 1. Rows are offset so the result is a visually
 * rectangular block rather than a rhombus.
 */
export function rectangularMapCoords(width, height) {
    const coords = [];
    for (let r = 0; r < height; r++) {
        const rowOffset = Math.floor(r / 2);
        for (let q = -rowOffset; q < width - rowOffset; q++) {
            coords.push({ q, r });
        }
    }
    return coords;
}
//# sourceMappingURL=hexGrid.js.map